import { createAsyncThunk, createSlice } from '@reduxjs/toolkit'
import apiService from 'services/api'
import localStorageService from 'services/storage'
import { DUMMY_ZONE_NAME } from './constants'

const SLICE_KEY = 'global'

const DEFAULT_ALERT_TIMEOUT = 3000

// Pre-mount currentUser request (IVX-9754). app.tsx calls primeCurrentUser()
// before React mounts, so the auth round-trip is already in flight while the
// bundle finishes downloading/parsing. fetchCurrentUser then adopts this
// promise instead of firing a second request.
let earlyCurrentUserPromise = null

export const primeCurrentUser = () => {
  if (!earlyCurrentUserPromise) {
    earlyCurrentUserPromise = apiService.currentUser()
    // Swallow here to avoid an unhandled-rejection warning in the brief window
    // before the thunk adopts (and re-handles) it; the original promise still
    // rejects for the awaiter below.
    earlyCurrentUserPromise.catch(() => {})
  }
  return earlyCurrentUserPromise
}

// The config/transmission calls below only need auth (not the currentUser
// body), so they no longer drive the global loading overlay — the dashboard
// shell renders as soon as currentUser is present and isn't held for this
// config wave (IVX-9754).
export const configureMap = createAsyncThunk(`${SLICE_KEY}/configureMap`, async () => {
  const { mapBaseUrl, mapZoomDelta } = await apiService.configureMap()

  return { mapBaseUrl, mapZoomDelta }
})

export const configurePlatform = createAsyncThunk(`${SLICE_KEY}/configurePlatform`, async () => {
  const { platform, hiddenVerticals } = await apiService.configurePlatform()

  return { platform, hiddenVerticals }
})

export const configureStaticFiles = createAsyncThunk(`${SLICE_KEY}/configureStaticFiles`, async () => {
  const { staticFilesUrl } = await apiService.configureStaticFiles()

  return { staticFilesUrl }
})

export const fetchZones = createAsyncThunk(`${SLICE_KEY}/fetchZones`, async (args, { dispatch }) => {
  dispatch(setIsLoadingZones(true))

  const { zones } = await apiService.zones()

  dispatch(setIsLoadingZones(false))

  return { zones }
})

export const fetchGroups = createAsyncThunk(`${SLICE_KEY}/fetchGroups`, async (args, { dispatch }) => {
  dispatch(setIsLoadingGroups(true))

  const { groups } = await apiService.groups()

  dispatch(setIsLoadingGroups(false))

  return { groups }
})

export const fetchOrganizations = createAsyncThunk(`${SLICE_KEY}/fetchOrganizations`, async (args, { dispatch }) => {
  dispatch(setIsLoadingOrganizations(true))

  const { organizations } = await apiService.organizations()

  dispatch(setIsLoadingOrganizations(false))

  return { organizations }
})

export const fetchUsers = createAsyncThunk(`${SLICE_KEY}/fetchUsers`, async (args, { dispatch }) => {
  dispatch(setIsLoadingUsers(true))

  const { users } = await apiService.users()

  dispatch(setIsLoadingUsers(false))

  return { users }
})

export const fetchConfigurationGlobals = createAsyncThunk(
  `${SLICE_KEY}/fetchConfigurationGlobals`,
  async (args, { dispatch }) => {
    const asyncMethods = [fetchZones, fetchOrganizations, fetchUsers, fetchGroups]
    await Promise.all(asyncMethods.map(func => dispatch(func())))
  },
)

export const fetchTransmissions = createAsyncThunk(`${SLICE_KEY}/fetchTransmissions`, async () => {
  const { transmissions } = await apiService.dashboardTransmissions()
  return { transmissions }
})

export const fetchCurrentUser = createAsyncThunk(`${SLICE_KEY}/fetchCurrentUser`, async (args, { dispatch }) => {
  // Adopt the request kicked off before mount (IVX-9754); fall back to a fresh
  // call for any later dispatch. One-shot: cleared as soon as it's consumed.
  const primed = earlyCurrentUserPromise
  earlyCurrentUserPromise = null
  try {
    const { currentUser, availableZones } = await (primed || apiService.currentUser())
    await dispatch(setCurrentUser({ currentUser, availableZones }))
    if (currentUser?.email) {
      dispatch(setCurrentUserStatus(CURRENT_USER_STATUS.LOGGED_IN))
    }
    localStorageService.clearReturnUrl()
    dispatch(setAuthError(null))
  } catch (e) {
    const statusCode = e?.response?.status || e?.status
    if (statusCode === 403) {
      const errorMessage =
        e?.response?.data?.message || e?.message || 'You do not have permission to access this application.'
      dispatch(setAuthError({ type: 'forbidden', message: errorMessage }))
      dispatch(setCurrentUserStatus(CURRENT_USER_STATUS.LOGGED_OUT))
    } else {
      dispatch(setAuthError(null))
      dispatch(setCurrentUserStatus(CURRENT_USER_STATUS.LOGGED_OUT))
    }
  }
})

export const addAlert = createAsyncThunk(`${SLICE_KEY}/addAlert`, async (alert, { dispatch }) => {
  const alertId = Date.now()
  dispatch(createAlert({ id: alertId, ...alert }))

  setTimeout(() => {
    dispatch(removeAlert({ id: alertId }))
  }, DEFAULT_ALERT_TIMEOUT)
})

// TODO convert to Typescript enum
export const CURRENT_USER_STATUS = {
  PENDING: 'pending',
  LOGGED_IN: 'loggedIn',
  LOGGED_OUT: 'loggedOut',
  PROVISIONAL: 'provisional',
}

export const initialState = {
  mapBaseUrl: '',
  platform: '',
  hiddenVerticals: [],
  staticFilesUrl: '',
  mapZoomDelta: 1,
  currentUser: {},
  currentUserStatus: CURRENT_USER_STATUS.PENDING,
  authError: null,
  availableZones: [],
  isGlobalLoading: false,
  isLoggingIn: false,
  isInitialized: false,
  alerts: {},
  isLoadingZones: false,
  currentZone: null,
  dashboardTransmissions: [],
  isLoadingOrganizations: false,
  organizations: [],
  currentOrganization: {},
  isLoadingUsers: false,
  users: [],
  userPlatform: null,
  allGroups: [],
  isLoadingGroups: false,
}

const slice = createSlice({
  name: SLICE_KEY,
  initialState,
  reducers: {
    setGlobalLoading(state, { payload: isLoading = false }) {
      state.isGlobalLoading = isLoading
    },
    setIsLoggingIn(state, { payload: isLoggingIn = false }) {
      state.isLoggingIn = isLoggingIn
    },
    setCurrentUser(state, { payload = {} }) {
      const { currentUser, availableZones = [] } = payload
      state.currentUser = currentUser
      state.availableZones = availableZones
      if (availableZones.length === 1) state.currentZone = availableZones[0]
      else state.currentZone = availableZones.find(({ name }) => name === DUMMY_ZONE_NAME)
    },
    setCurrentUserStatus(state, { payload }) {
      state.currentUserStatus = payload
    },
    setCurrentOrganization(state, { payload = {} }) {
      const { currentUser, organizations } = payload

      const { organizationId } = currentUser
      if (!organizationId) return

      const currentOrganization = organizations.find(({ id }) => id === organizationId)

      if (currentOrganization) state.currentOrganization = currentOrganization
    },
    setIsLoadingZones(state, { payload: isLoading = false }) {
      state.isLoadingZones = isLoading
    },
    setIsLoadingOrganizations(state, { payload: isLoading = false }) {
      state.isLoadingOrganizations = isLoading
    },
    setIsLoadingUsers(state, { payload: isLoading = false }) {
      state.isLoadingUsers = isLoading
    },
    setIsLoadingGroups(state, { payload: isLoading = false }) {
      state.isLoadingGroups = isLoading
    },
    setIsInitialized(state, { payload: isInitialized = false }) {
      state.isInitialized = isInitialized
    },
    setAuthError(state, { payload: error = null }) {
      state.authError = error
    },
    createAlert(state, { payload = {} }) {
      const { id } = payload
      state.alerts[id] = payload
    },
    removeAlert(state, { payload = {} }) {
      const { id } = payload
      delete state.alerts[id]
    },
    setCurrentZone(state, { payload = {} }) {
      const { zone } = payload
      state.currentZone = zone
    },
    onOrganizationsPageUnmount(state) {
      state.isLoadingZones = true
      state.isLoadingOrganizations = true
      state.isLoadingUsers = true
    },
    setUserPlatform(state, { payload = {} }) {
      const { platform } = payload
      state.userPlatform = platform
    },
  },
  extraReducers: builder => {
    builder
      .addCase(configureMap.fulfilled, (state, { payload = {} }) => {
        const { mapBaseUrl, mapZoomDelta } = payload
        state.mapBaseUrl = mapBaseUrl
        state.mapZoomDelta = parseFloat(mapZoomDelta)
      })
      .addCase(configurePlatform.fulfilled, (state, { payload = {} }) => {
        const { platform, hiddenVerticals } = payload
        state.platform = platform
        state.hiddenVerticals = hiddenVerticals ?? []
      })
      .addCase(configureStaticFiles.fulfilled, (state, { payload = {} }) => {
        const { staticFilesUrl } = payload
        state.staticFilesUrl = staticFilesUrl
      })
      .addCase(fetchZones.fulfilled, (state, { payload = {} }) => {
        const { zones = [] } = payload
        state.availableZones = zones.map(zone => {
          const origZone = state.availableZones.find(z => z.id === zone.id)
          if (origZone) return { ...origZone, ...zone }
          return zone
        })
      })
      .addCase(fetchGroups.fulfilled, (state, { payload = {} }) => {
        const { groups } = payload
        state.allGroups = groups
      })
      .addCase(fetchOrganizations.fulfilled, (state, { payload = {} }) => {
        const { organizations } = payload
        state.organizations = organizations
      })
      .addCase(fetchUsers.fulfilled, (state, { payload = {} }) => {
        const { users } = payload
        state.users = users
      })
      .addCase(fetchTransmissions.fulfilled, (state, { payload = {} }) => {
        const { transmissions } = payload
        state.dashboardTransmissions = transmissions
      })
      .addCase('organizations/submitUser/rejected', state => {
        state.isGlobalLoading = false
      })
  },
})

export const {
  setIsInitialized,
  setGlobalLoading,
  setIsLoggingIn,
  setIsLoadingZones,
  setIsLoadingGroups,
  setIsLoadingOrganizations,
  setIsLoadingUsers,
  removeAlert,
  createAlert,
  setCurrentZone,
  setCurrentUser,
  setCurrentUserStatus,
  setAuthError,
  setCurrentOrganization,
  onOrganizationsPageUnmount,
  setUserPlatform,
} = slice.actions

export const globalReducer = slice.reducer
